/**
 * @deprecated This component is being replaced by the unified CDS-based
 * `SearchBarContentWrapper` (search-common) via the Workstream 2 autocomplete
 * migration. Scheduled for deletion in https://coursera.atlassian.net/browse/SEARCH-2179 after the adapter swap lands.
 */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { breakpoints } from '@coursera/cds-core';

import EnterpriseAutoComplete from 'bundles/enterprise-learner-search/components/search/EnterpriseAutoComplete';
import type { SearchScope } from 'bundles/enterprise-learner-search/components/search/types';
import {
  type UseUnifiedSearchBarPropsParams,
  useUnifiedSearchBarProps,
} from 'bundles/enterprise-learner-search/components/search/useUnifiedSearchBarProps';

import _t from 'i18n!nls/enterprise-learner-search';

type Props = {
  scopes: SearchScope[] | undefined;
  initialScopeIndex: number | undefined;
  enableSkillsInSearchAndBrowse?: boolean;
  shouldShowShortFormContent?: boolean;
  isMobile?: boolean;
  shouldHideSearchScope?: boolean;
  testId?: string;
  forceFullWidthMobile?: boolean;
};

type State = {
  hasMounted: boolean;
};

const styles = {
  containerFullWidth: css`
    max-width: 100% !important;
  `,
  container: css`
    margin: 14px 0;
    background-color: var(--cds-color-neutral-background-primary);
    height: 36px;
    padding-left: var(--cds-spacing-50);
    padding-right: var(--cds-spacing-50);
    display: flex;
    flex-grow: 1;
    flex-flow: row nowrap;

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-100);
      height: auto;
    }

    .scope-select {
      background-color: var(--cds-color-neutral-background-primary);
      border: 1px solid var(--cds-color-neutral-stroke-primary);
      border-right: none;
      border-radius: var(--cds-border-radius-max) 0 0 var(--cds-border-radius-max);
      box-sizing: border-box;
      height: var(--cds-spacing-50);
      max-width: var(--cds-spacing-150);
      padding: 0 var(--cds-spacing-150);
      text-overflow: ellipsis;

      ${breakpoints.down('xs')} {
        max-width: var(--cds-spacing-100);
        padding: 0 var(--cds-spacing-150);
      }

      html[dir='rtl'] & {
        border-radius: 0 var(--cds-border-radius-max) var(--cds-border-radius-max) 0;
        border-right: 1px solid var(--cds-color-neutral-stroke-primary);
        border-left: none;
      }
    }

    .rc-EnterpriseAutoComplete {
      flex-grow: 1;
    }

    .react-autosuggest__input {
      width: 100%;
      text-overflow: ellipsis;
    }

    .svg-container {
      display: flex;
      align-items: center;
    }
  `,
  mobileSearchBar: css`
    background: transparent;
    padding: 0;
    margin: 0;
  `,
  desktopSearchBar: css`
    height: auto;
    margin: 0;
  `,
};

export class EnterpriseSearchBarImpl extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasMounted: false,
    };
  }

  componentDidMount() {
    this.setState({ hasMounted: true });
  }

  render() {
    const {
      forceFullWidthMobile,
      scopes,
      enableSkillsInSearchAndBrowse,
      shouldShowShortFormContent,
      isMobile,
      testId = 'enterprise-search-bar-v2',
    } = this.props;
    const { hasMounted } = this.state;
    const scope = scopes?.[0];
    const shouldRenderScopeSelect = Number(scopes?.length) > 1 && !this.props.shouldHideSearchScope;

    const placeholder = scope?.pathname.includes('programs/all/')
      ? _t('Search All Learning Programs')
      : _t('Search Coursera for #{programName}', { programName: scope?.title });

    return (
      <div
        className={`rc-EnterpriseSearchBarV2 ${shouldRenderScopeSelect ? '--scope' : '--no-scope'}`}
        data-testid={testId}
        css={[
          styles.container,
          ...[
            isMobile ? styles.mobileSearchBar : styles.desktopSearchBar,
            forceFullWidthMobile && styles.containerFullWidth,
          ],
        ]}
      >
        {scope && hasMounted ? (
          <EnterpriseAutoComplete
            pathname={scope.pathname}
            programIds={scope.programIds}
            enableSkillsInSearchAndBrowse={enableSkillsInSearchAndBrowse}
            shouldShowShortFormContent={shouldShowShortFormContent}
            shouldRenderScopeSelect={shouldRenderScopeSelect}
            placeholder={placeholder}
            forceFullWidthMobile={forceFullWidthMobile}
          />
        ) : null}
      </div>
    );
  }
}

type LegacyEnterpriseSearchBarPropsFromCaller = {
  programIds: Array<string>;
  enableSkillsInSearchAndBrowse: boolean;
  shouldShowShortFormContent?: boolean;
  programName?: string;
};

export const EnterpriseSearchBarV2 = ({
  programIds,
  enableSkillsInSearchAndBrowse,
  shouldShowShortFormContent,
  programName,
}: LegacyEnterpriseSearchBarPropsFromCaller) => {
  const { programSlug } = useParams();

  if (!programSlug) {
    return null;
  }

  return (
    <EnterpriseSearchBarImpl
      scopes={[
        {
          trackingData: { programSlug },
          placeholder: _t('What do you want to learn?'),
          title: programName || '',
          pathname: `/programs/${programSlug}`,
          programIds,
        },
      ]}
      enableSkillsInSearchAndBrowse={enableSkillsInSearchAndBrowse}
      initialScopeIndex={0}
      shouldShowShortFormContent={shouldShowShortFormContent}
    />
  );
};

type UnifiedEnterpriseSearchBarProps = UseUnifiedSearchBarPropsParams & {
  shouldShowShortFormContent?: boolean;
  isMobile?: boolean;
  shouldHideSearchScope?: boolean;
  testId?: string;
  forceFullWidthMobile?: boolean;
};

export const UnifiedEnterpriseSearchBar = ({
  programSlug,
  programName,
  thirdPartyOrgSlug,
  thirdPartyOrganizationId,
  userId,
  programId,
  enableSkillsInSearchAndBrowse,
  shouldShowShortFormContent,
  isMobile,
  shouldHideSearchScope,
  testId,
  forceFullWidthMobile,
}: UnifiedEnterpriseSearchBarProps) => {
  const unifiedProps = useUnifiedSearchBarProps({
    programSlug,
    programName,
    thirdPartyOrgSlug,
    thirdPartyOrganizationId,
    userId,
    programId,
    enableSkillsInSearchAndBrowse,
  });

  return (
    <EnterpriseSearchBarImpl
      {...unifiedProps}
      shouldShowShortFormContent={shouldShowShortFormContent}
      isMobile={isMobile}
      shouldHideSearchScope={shouldHideSearchScope}
      {...(testId && { testId })}
      forceFullWidthMobile={forceFullWidthMobile}
    />
  );
};

export default EnterpriseSearchBarV2;
